// ─────────────────────────────────────────────────────────────
// WelcomeTab — bento-grid home screen.
// Layout: hero + quick-start | prompt chips + featured scripts
// ─────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Divider,
  Typography,
  Link,
} from "@mui/material";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { MarieIcon } from "../../components/MarieIcon";
import type { SxProps, Theme } from "@mui/material";
import { authApi, communityApi, imageApi, libraryScriptsApi } from "../../api/client";
import type { AuthUser, CommunityScript, LibraryScript } from "../../api/client";
import { StarRating } from "./StarRating";
import { AuthorAvatar } from "./AuthorAvatar";
import { VideoModal } from "./VideoModal";
import type { OnboardingVideo } from "./VideoModal";
import { formatRelativeTime } from "../../utils/formatRelativeTime";

interface WelcomeTabProps {
  onNavigateToPython: () => void;
  /**
   * Navigate to the Scripts tab, landing on a specific sub-tab.
   * Sub-tab indices match ScriptsTab: 0 = My Scripts, 1 = Starter Scripts.
   */
  onNavigateToScripts: (subTab: 0 | 1) => void;
  /** Navigate to the top-level Community tab (where the "Browse all" link in the Featured Community Scripts cell points). */
  onNavigateToCommunity: () => void;
  onAiPrompt: (prompt: string) => void;
  /** Generic load callback — used by the Quick Start cell (starter library scripts). */
  onLoadScript: (code: string, defaultImageId?: number | null, savedScriptId?: number | null, scriptTitle?: string) => void;
  /**
   * Community-sourced load callback — used by the Featured Community Scripts
   * cell. ScriptHelperApp pipes this through the Marie introduce-script
   * endpoint so the python tab's AI panel gets a Marie-voiced credit for
   * the author. Kept separate from `onLoadScript` so starter Quick-Start
   * cards (which are NOT community-sourced) don't accidentally fire the
   * community intro path.
   */
  onLoadCommunityScript: (script: CommunityScript) => void;
  onChatWithMarie: () => void;
  /**
   * The signed-in user. The Welcome tab no longer auto-opens the intro
   * modal — discovery happens entirely through the in-hero "Watch the
   * intro" button. When `has_seen_welcome_video === false` the button
   * renders in a prominent state (contained primary, larger, soft
   * breathing pulse). Once the user clicks it the flag flips and the
   * button collapses to the subtle outlined state on the next render.
   */
  currentUser: AuthUser;
  /**
   * Called the moment the user clicks an in-hero onboarding-video button
   * — either "Watch the intro" or "Quickstart" (NOT on modal close —
   * clicking IS the engagement). Receives a full AuthUser so the parent
   * can mirror the flip into both React state and localStorage; the
   * parent writer is video-agnostic (it just stores whatever AuthUser it
   * gets), so this single callback serves both videos. Each button fires
   * it twice: once optimistically with the relevant `*_video` flag
   * flipped, then again with the server's authoritative AuthUser. Each
   * button has its own single-flight ref so re-opens during the
   * round-trip don't fire duplicate POSTs.
   */
  onWelcomeVideoSeen: (updated: AuthUser) => void;
}

// ── Placeholder data ─────────────────────────────────────────

// Quick Start cards are keyed by TITLE, not numeric id. The library_scripts
// seed reorders IDs every time it's re-run, so a hardcoded `scriptId: 10`
// silently drifts to a different script after any seed change. Looking up
// by title means a future rename produces a clean "script not found" no-op
// instead of loading the wrong script under the right card title.
const QUICK_START_CARDS = [
  {
    id: "qs1",
    title: "Particle Circularity Report",
    description: "Detect particles, measure circularity, and emit four outputs at once — image, overlay, CSV, and a PDF report.",
  },
  {
    id: "qs2",
    title: "Multi-Channel Output",
    description: "Split an image into colour channels that composite into a single layer in Maps.",
  },
  {
    id: "qs3",
    title: "Propose a Tile Set",
    description: "Find the busiest regions of an image and propose high-resolution acquisition scans.",
  },
];

const AI_PROMPT_CHIPS = [
  "How do I access pixel data?",
  "Write a script to apply Gaussian blur",
  "How do I save output images?",
  "Explain the MapsBridge API",
  "Show me how to iterate over channels",
  "How do I add custom parameters?",
];

// Pure helper — kept at module scope since it has no React deps. Exact-match
// title lookup against the flattened library list. Returns null if nothing
// matches (e.g. the starter script was renamed in a later seed).
function findScriptByTitle(scripts: LibraryScript[], title: string): LibraryScript | null {
  return scripts.find((s) => s.title === title) ?? null;
}

// ── Featured community script selection ───────────────────────
//
// The Welcome "Featured Community Scripts" cell shows a deliberate
// slotted mix of 4 cards instead of a flat top-4-by-rating slice
// (which froze the same scripts forever and hid new publishes):
//
//   Slot 1 — Top rated. communityApi.list() is already server-sorted
//     by avg_rating DESC, then rating_count DESC, then created_at
//     DESC — so list[0] IS the top-rated script. No client sort.
//   Slot 2 — Most forked. The script with the highest `fork_count`
//     not already picked. Graceful fallback: fork counts are
//     forward-only from launch, so right now every script sits at
//     fork_count 0 — a flat tie. When the best available fork_count
//     is 0 (nothing forked yet), this slot does NOT label an
//     arbitrary 0-fork script as "most forked" — it degrades to an
//     extra random wildcard. The genuine most-forked card appears
//     automatically once forks start accumulating; no later code
//     change needed.
//   Slot 3 — Recently shared. The newest script not already picked.
//     Recency is `created_at` descending — mirrors the Community
//     tab's "Recently shared" lane exactly so the two surfaces
//     agree on what "newest" means.
//   Slot 4 — Random wildcard. A random pick from whatever's left.
//
// No duplicates: slots fill in order 1 → 2 → 3 → 4, each skipping
// anything already chosen. Small corpus: fewer than 4 public
// scripts → as many unique cards as can be filled (the caller keeps
// the existing zero-scripts empty state).
//
// Pure except for the Math.random() wildcard picks. The caller
// wraps sessionStorage persistence around this so the set is
// stable within a browser session and re-rolls on a fresh one.
const FEATURED_SCRIPT_COUNT = 4;

function selectFeaturedScripts(allScripts: CommunityScript[]): CommunityScript[] {
  if (allScripts.length === 0) return [];

  const picked: CommunityScript[] = [];
  const usedIds = new Set<number>();

  function take(script: CommunityScript | undefined) {
    if (script && !usedIds.has(script.id) && picked.length < FEATURED_SCRIPT_COUNT) {
      picked.push(script);
      usedIds.add(script.id);
    }
  }

  // Pick a random script from those not yet used. Returns undefined
  // when the pool is exhausted.
  function takeWildcard() {
    const remaining = allScripts.filter((s) => !usedIds.has(s.id));
    if (remaining.length > 0) {
      take(remaining[Math.floor(Math.random() * remaining.length)]);
    }
  }

  // Slot 1 — top rated. The server sort means the first entry of the
  // list is already the highest-rated script.
  take(allScripts[0]);

  // Slot 2 — most forked. Highest `fork_count` not already picked.
  // If that best fork_count is 0, nothing has actually been forked
  // yet → degrade this slot to an extra wildcard rather than
  // mislabel a 0-fork script as "most forked".
  const mostForkedFirst = [...allScripts].sort((a, b) => b.fork_count - a.fork_count);
  const topForkCandidate = mostForkedFirst.find((s) => !usedIds.has(s.id));
  if (topForkCandidate && topForkCandidate.fork_count > 0) {
    take(topForkCandidate);
  } else {
    takeWildcard();
  }

  // Slot 3 — recently shared. Newest by `created_at` desc, mirroring
  // the Community tab's "Recently shared" lane sort comparator.
  const newestFirst = [...allScripts].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  take(newestFirst.find((s) => !usedIds.has(s.id)));

  // Slot 4 — random wildcard from whatever remains.
  takeWildcard();

  return picked;
}

// sessionStorage key for the per-session-pinned featured selection.
// Holds the chosen script IDs as a JSON number[]. sessionStorage
// clears when the browser session ends → natural per-session
// re-roll. localStorage would pin the set permanently; that's not
// what we want.
const FEATURED_SESSION_KEY = "welcome_featured_script_ids";

// Resolve a sessionStorage-saved selection against the freshly
// fetched community list. Returns the script objects (re-resolved
// from fresh data so ratings/metadata stay current) only if a
// saved selection exists AND every saved ID still resolves to a
// live script. Otherwise null → caller recomputes + re-saves.
function resolveSavedFeatured(allScripts: CommunityScript[]): CommunityScript[] | null {
  let savedIds: number[];
  try {
    const raw = sessionStorage.getItem(FEATURED_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.some((id) => typeof id !== "number")) return null;
    savedIds = parsed;
  } catch {
    return null;
  }
  if (savedIds.length === 0) return null;

  const byId = new Map(allScripts.map((s) => [s.id, s]));
  const resolved: CommunityScript[] = [];
  for (const id of savedIds) {
    const script = byId.get(id);
    // Any saved script that's been deleted/unpublished invalidates
    // the whole saved set — recompute rather than show a short list.
    if (!script) return null;
    resolved.push(script);
  }

  // Count-mismatch invalidation: a pin made when the corpus was small
  // (e.g. only 3 public scripts) stays a short set forever even after
  // more scripts are published, leaving the featured cell under-filled.
  // If the saved selection is shorter than the cell should hold given
  // the current corpus size, treat it as stale → recompute + re-save.
  if (savedIds.length < Math.min(FEATURED_SCRIPT_COUNT, allScripts.length)) {
    return null;
  }

  return resolved;
}

// ── Bento cell helper ─────────────────────────────────────────
// Surface tier selects the MD3 surface container token used for
// the cell background. Depth comes from surface differentiation
// (not from elevation shadows or heavy borders).

type SurfaceTier =
  | "surfaceContainerLowest"
  | "surfaceContainerLow"
  | "surfaceContainer"
  | "surfaceContainerHigh"
  | "surfaceContainerHighest";

function BentoCell({
  children,
  surface = "surfaceContainer",
  sx,
}: {
  children: React.ReactNode;
  surface?: SurfaceTier;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      sx={[
        {
          bgcolor: surface,
          // Hairline border kept for tonal modes where the
          // surface delta is close (e.g. user-zoomed dark
          // mode, OS contrast settings) — softer than divider
          // so the surface tone leads, not the line.
          border: "1px solid",
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.10)"
              : "rgba(0,0,0,0.10)",
          borderRadius: 1,
          p: 2,
          overflow: "hidden",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}

// ── Featured card side image panel ────────────────────────────
//
// Full-height image panel rendered on the right edge (~38% width) of
// each Welcome featured-community card. Replaces the earlier 74x74
// upper-right corner thumbnail — Greg's redesign wants the image to
// read as a clean cropped vertical slice of the card rather than a
// small floating accent.
//
// Deliberately still distinct from the Community tab's CommunityCard,
// which uses a full-width TOP banner. On Welcome the cards live in a
// 2-col grid, so a side panel gives a stronger visual hook while
// keeping the author / title / description column intact on the left.
//
// Behavior:
//   1. Script has `default_image_id` → fetch the 400px thumbnail
//      (blob URL via imageApi.getThumbnailUrl) and render it as a
//      cover-fit full-height image. Falls back to the placeholder on
//      fetch error.
//   2. No paired image → quiet surfaceContainerLow panel with a
//      centered ImageOutlined icon scaled up for the larger surface.
//
// The blob URL is revoked on unmount / id change so we don't leak
// object URLs as the featured set re-fetches.

interface FeaturedSideImageProps {
  imageId: number | null;
}

function FeaturedSideImage({ imageId }: FeaturedSideImageProps) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    setThumbUrl(null);
    setLoadFailed(false);
    if (imageId == null) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    imageApi
      .getThumbnailUrl(imageId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setThumbUrl(url);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [imageId]);

  const showPlaceholder = imageId == null || loadFailed;

  return (
    <Box
      sx={{
        // ~28% of the card width — a vertical slice that's visible
        // without dominating the card. The left content column gets the
        // remaining ~72% via `flex: 1`. flexShrink/Grow are pinned so
        // the panel never collapses when the description is long.
        flexBasis: "28%",
        flexGrow: 0,
        flexShrink: 0,
        // Stretch to the full card height (the card is a flex row, so
        // alignSelf: stretch — the default — already gives us this;
        // height: 100% makes it explicit for the inner img/icon).
        alignSelf: "stretch",
        bgcolor: "surfaceContainerLow",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        // The card clips the outer corners via its own borderRadius +
        // overflow: hidden, so the panel itself needs no radius.
        pointerEvents: "none",
      }}
      aria-hidden
    >
      {showPlaceholder ? (
        <ImageOutlinedIcon
          sx={{ fontSize: 44, color: "text.disabled" }}
          aria-hidden
        />
      ) : thumbUrl ? (
        <Box
          component="img"
          src={thumbUrl}
          alt=""
          loading="lazy"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
          onError={() => setLoadFailed(true)}
        />
      ) : (
        // Loading state — quiet surfaceContainerLow fill, no spinner.
        // The panel is a decorative accent; a spinner there would draw
        // the eye away from the title.
        <Box sx={{ width: "100%", height: "100%" }} aria-hidden />
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────

export function WelcomeTab({ onNavigateToPython, onNavigateToScripts, onNavigateToCommunity, onAiPrompt, onLoadScript, onLoadCommunityScript, onChatWithMarie, currentUser, onWelcomeVideoSeen }: WelcomeTabProps) {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [featuredScripts, setFeaturedScripts] = useState<CommunityScript[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  // Which onboarding video the (single, shared) modal is currently
  // showing. Set the moment a video button is clicked, read by the
  // <VideoModal> at the bottom of the render.
  const [activeVideo, setActiveVideo] = useState<OnboardingVideo>("welcome");

  // Single-flight guards so re-opens during the POST round-trip don't fire
  // duplicate POSTs. One per video — /api/users/me/welcome-video-seen and
  // /api/users/me/quickstart-video-seen are independent.
  const hasMarkedSeenRef = useRef(false);
  const hasMarkedQuickstartSeenRef = useRef(false);

  // Optimistic + authoritative flip. The parent owns the AuthUser, so we
  // call back with an optimistic copy immediately (so the button collapses
  // to its subtle state on the next render), then call back again with the
  // server response when it lands. If the request fails we silently
  // ignore — worst case the prominent button reappears on next page load,
  // and the backend is idempotent so a retry is harmless.
  function markSeen() {
    if (hasMarkedSeenRef.current) return;
    if (currentUser.has_seen_welcome_video) {
      // Already seen — no need to POST again. The backend is idempotent
      // but we may as well skip the round trip.
      hasMarkedSeenRef.current = true;
      return;
    }
    hasMarkedSeenRef.current = true;
    onWelcomeVideoSeen({ ...currentUser, has_seen_welcome_video: true });
    authApi
      .markWelcomeVideoSeen()
      .then((updated) => onWelcomeVideoSeen(updated))
      .catch(() => {
        // Non-critical — optimistic state already applied
      });
  }

  // Quickstart-video mirror of markSeen. Same optimistic-then-authoritative
  // pattern, its own single-flight ref, its own backend endpoint. Routed
  // through the same video-agnostic onWelcomeVideoSeen parent writer.
  function markQuickstartSeen() {
    if (hasMarkedQuickstartSeenRef.current) return;
    if (currentUser.has_seen_quickstart_video) {
      hasMarkedQuickstartSeenRef.current = true;
      return;
    }
    hasMarkedQuickstartSeenRef.current = true;
    onWelcomeVideoSeen({ ...currentUser, has_seen_quickstart_video: true });
    authApi
      .markQuickstartVideoSeen()
      .then((updated) => onWelcomeVideoSeen(updated))
      .catch(() => {
        // Non-critical — optimistic state already applied
      });
  }

  function handleModalClose() {
    // Clicking the in-hero button already fired markSeen — closing the
    // modal is purely a UI dismissal at this point.
    setVideoModalOpen(false);
  }

  // Fetch + select the featured community scripts on mount.
  //
  // Selection is a slotted mix (1 top-rated + 1 most-forked + 1
  // recent + 1 random wildcard — see selectFeaturedScripts) rather
  // than a flat top-4-by-rating slice. The chosen set is pinned per browser
  // session via sessionStorage so navigating away from and back to
  // Welcome shows the same 4 cards, but a fresh session re-rolls
  // the wildcard (and re-evaluates the whole set).
  //
  // On every mount we still re-fetch the full list so the resolved
  // script objects carry current ratings/metadata. If the saved
  // selection is missing or any saved ID no longer resolves (script
  // deleted/unpublished), we recompute and re-save.
  useEffect(() => {
    let cancelled = false;
    communityApi
      .list()
      .then((data) => {
        if (cancelled) return;
        const saved = resolveSavedFeatured(data);
        if (saved) {
          setFeaturedScripts(saved);
        } else {
          const selection = selectFeaturedScripts(data);
          setFeaturedScripts(selection);
          try {
            sessionStorage.setItem(
              FEATURED_SESSION_KEY,
              JSON.stringify(selection.map((s) => s.id)),
            );
          } catch {
            // sessionStorage unavailable (private mode / quota) —
            // non-critical, the selection just won't pin this session.
          }
        }
      })
      .catch(() => {
        // Non-critical — just leave empty, show the empty state
      })
      .finally(() => {
        if (!cancelled) setFeaturedLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  async function handleQuickStart(card: typeof QUICK_START_CARDS[0]) {
    if (loadingId) return;
    setLoadingId(card.id);
    try {
      const data = await libraryScriptsApi.list();
      const allScripts = [
        ...data.categories.flatMap((c) => c.scripts),
        ...data.uncategorized,
      ];
      // Title is the authoritative key — see comment on QUICK_START_CARDS.
      // If a future seed renames a starter script, the card no-ops with a
      // console warn instead of silently loading the wrong script.
      const script = findScriptByTitle(allScripts, card.title);
      if (script) {
        onLoadScript(script.code, script.default_image_id, null, script.title);
        onNavigateToPython();
      } else {
        console.warn(`Quick Start: no library script matches title "${card.title}"`);
      }
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        gridTemplateRows: "auto auto",
        gap: 1.5,
        p: 1.5,
        height: "100%",
        overflow: "auto",
        alignContent: "start",
      }}
    >
      {/* ── Hero (top-left, 2/3 width = 2 cols) ─────────────── */}
      {/*
        Hero panel uses a subtle bottom-left → top-right gradient that
        spans one tier below and one tier above the previous flat
        `surfaceContainerHigh` background. Brackets the original tone
        so the panel's average lightness is unchanged but gains a
        directional sheen that reads in both light and dark mode.
      */}
      <BentoCell
        surface="surfaceContainerHigh"
        sx={{
          gridColumn: "1 / span 8",
          gridRow: "1",
          // Hero panel intentionally tall (~360px) so the bottom CTA
          // row (Open Python Editor + Chat with Marie) reads as a pair
          // of more "square / button-shaped" tiles rather than the
          // earlier short horizontal stripes. The CTA row uses
          // `flex: 1` so it absorbs the extra vertical room — the
          // hero copy block stays anchored to the top while the CTA
          // cards grow into the bottom half.
          minHeight: 360,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? `linear-gradient(to top right, ${theme.palette.surfaceContainerHigh}, ${theme.palette.surfaceContainerHighest})`
              : `linear-gradient(to top right, ${theme.palette.surfaceContainerHigh}, #ffffff)`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
          <Box>
            <Typography variant="overline" color="primary.main">
              Maps Microscopy Platform
            </Typography>
            <Typography variant="h1" component="h1" sx={{ mt: 0.5 }}>
              Maps Discover Studio
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 560 }}>
              Write, test, and share Python image-processing scripts for the Thermo Fisher Scientific Maps microscopy platform.
              Marie understands the Maps Script Bridge API — ask her anything.
            </Typography>

            {/*
              In-hero "Watch the intro" button. Two visual states keyed
              off currentUser.has_seen_welcome_video:

              • Prominent (flag === false) — contained primary, size
                large, filled PlayCircle icon at 28px, bold label, and a
                soft 2.4s breathing pulse (box-shadow). The pulse is
                gated behind prefers-reduced-motion: reduce so motion-
                sensitive users get the static contained button. This is
                the FIRST-LOGIN discovery affordance — the auto-opening
                popover was removed in favor of letting the button itself
                grab the eye.

              • Subtle (flag === true) — small outlined button with the
                outlined PlayCircle icon. Same affordance for returning
                users who want to re-watch.

              Click fires markSeen IMMEDIATELY (not on modal close):
              clicking IS the engagement, so the next render collapses
              to the subtle state regardless of whether the user watches
              or dismisses.
            */}
            {/*
              Three-tier visibility for the in-hero "Watch the intro"
              button:

              • has_run_first_script === true → button hidden entirely.
                Returning users who've already run a script don't need a
                discovery affordance in the hero — the Help-tab sidebar
                button is the always-on path for re-watching.
              • has_run_first_script === false && has_seen_welcome_video
                === false → prominent (filled + pulse) discovery button.
              • has_run_first_script === false && has_seen_welcome_video
                === true → subtle outlined button for re-watch.
            */}
            {/*
              Onboarding-video button row. Holds the "Watch the intro"
              button (unchanged behavior) and, beside it, the gated
              "Quickstart" button. Both share the row's `has_run_first_script`
              gate — once the user is past onboarding the whole row drops
              out of the hero (the Help-tab sidebar is the always-on
              re-watch path for both videos).
            */}
            {!currentUser.has_run_first_script && (
              <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                {currentUser.has_seen_welcome_video ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlayCircleOutlinedIcon />}
                    onClick={() => {
                      setActiveVideo("welcome");
                      setVideoModalOpen(true);
                    }}
                    sx={{ textTransform: "none" }}
                    aria-label="Watch the intro video"
                  >
                    Watch the intro
                    <Box component="span" sx={{ ml: 1, opacity: 0.7, fontSize: "0.75rem" }}>
                      2 min
                    </Box>
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    disableElevation
                    startIcon={<PlayCircleIcon sx={{ fontSize: 28 }} />}
                    onClick={() => {
                      setActiveVideo("welcome");
                      setVideoModalOpen(true);
                      if (!hasMarkedSeenRef.current) {
                        markSeen();
                      }
                    }}
                    aria-label="Watch the intro video"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.9375rem",
                      px: 2.5,
                      py: 1,
                      borderRadius: "20px",
                      // Soft primary-tinted breathing pulse. Lives on the
                      // button's own box-shadow so it doesn't conflict
                      // with the hero panel's gradient or the adjacent
                      // CTA cards. ~2.4s cycle reads as "inviting" rather
                      // than urgent. Disabled for users with reduced-
                      // motion preference — they still get the contained
                      // primary treatment, which is plenty prominent on
                      // its own.
                      animation: "watch-intro-pulse 2.4s ease-in-out infinite",
                      "@keyframes watch-intro-pulse": {
                        "0%, 100%": {
                          boxShadow: (theme) =>
                            `0 0 0 0 ${theme.palette.primary.main}00`,
                        },
                        "50%": {
                          boxShadow: (theme) =>
                            `0 0 0 6px ${theme.palette.primary.main}33`,
                        },
                      },
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                      "&:hover": {
                        animation: "none",
                      },
                    }}
                  >
                    Watch the intro
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        opacity: 0.85,
                        fontSize: "0.8125rem",
                        fontWeight: 500,
                      }}
                    >
                      · 2 min
                    </Box>
                  </Button>
                )}

                {/*
                  Quickstart button — SEQUENCED behind the intro. Greg's
                  flow wants the user to watch the intro FIRST (it gives
                  context), so this button's prominence is gated on
                  has_seen_welcome_video. Three visible tiers (the
                  has_run_first_script === true case is handled by the
                  parent row's gate above — past-onboarding users see
                  neither button):

                  • has_seen_welcome_video === false → SUBDUED. A quiet
                    greyed text-link-style button: no pulse, no fill. Still
                    fully clickable — a user MAY watch the Quickstart first
                    — but the visual focus stays on the prominent pulsing
                    intro button beside it.

                  • has_seen_welcome_video === true &&
                    has_seen_quickstart_video === false → PROMINENT.
                    Contained primary, size large, breathing pulse —
                    exactly the treatment the intro button gets at first
                    login. This is the moment the Quickstart "becomes more
                    prominent": the intro is done, now watch this.

                  • has_seen_quickstart_video === true → SUBTLE. Small
                    outlined re-watch button, mirroring the intro's
                    seen-state.

                  Click fires markQuickstartSeen IMMEDIATELY (clicking IS
                  the engagement — same pattern as the intro) and opens
                  the shared modal to the Quickstart video.
                */}
                {currentUser.has_seen_quickstart_video ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlayCircleOutlinedIcon />}
                    onClick={() => {
                      setActiveVideo("quickstart");
                      setVideoModalOpen(true);
                    }}
                    sx={{ textTransform: "none" }}
                    aria-label="Watch the quickstart video"
                  >
                    Quickstart
                    <Box component="span" sx={{ ml: 1, opacity: 0.7, fontSize: "0.75rem" }}>
                      3 min
                    </Box>
                  </Button>
                ) : currentUser.has_seen_welcome_video ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    disableElevation
                    startIcon={<PlayCircleIcon sx={{ fontSize: 28 }} />}
                    onClick={() => {
                      setActiveVideo("quickstart");
                      setVideoModalOpen(true);
                      if (!hasMarkedQuickstartSeenRef.current) {
                        markQuickstartSeen();
                      }
                    }}
                    aria-label="Watch the quickstart video"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.9375rem",
                      px: 2.5,
                      py: 1,
                      borderRadius: "20px",
                      // Same breathing-pulse treatment as the prominent
                      // intro button — reused keyframes so the two
                      // affordances are visually identical when each is
                      // the "current" onboarding step.
                      animation: "watch-intro-pulse 2.4s ease-in-out infinite",
                      "@keyframes watch-intro-pulse": {
                        "0%, 100%": {
                          boxShadow: (theme) =>
                            `0 0 0 0 ${theme.palette.primary.main}00`,
                        },
                        "50%": {
                          boxShadow: (theme) =>
                            `0 0 0 6px ${theme.palette.primary.main}33`,
                        },
                      },
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                      "&:hover": {
                        animation: "none",
                      },
                    }}
                  >
                    Quickstart
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        opacity: 0.85,
                        fontSize: "0.8125rem",
                        fontWeight: 500,
                      }}
                    >
                      · 3 min
                    </Box>
                  </Button>
                ) : (
                  // Subdued — quiet text-style affordance. Still clickable.
                  // No pulse, no fill: it must not compete with the
                  // prominent intro button for the user's attention.
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<PlayCircleOutlinedIcon sx={{ fontSize: 18 }} />}
                    onClick={() => {
                      setActiveVideo("quickstart");
                      setVideoModalOpen(true);
                      if (!hasMarkedQuickstartSeenRef.current) {
                        markQuickstartSeen();
                      }
                    }}
                    aria-label="Watch the quickstart video"
                    sx={{
                      textTransform: "none",
                      color: "text.secondary",
                      fontWeight: 400,
                      "&:hover": {
                        color: "text.primary",
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    Quickstart
                    <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontSize: "0.75rem" }}>
                      3 min
                    </Box>
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Equal-height CTA cards — absorbs the extra vertical room
              in the taller hero so the two tiles read as more "square /
              button-shaped" rather than horizontal stripes. */}
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1.5, alignItems: "stretch", flex: 1, minHeight: 0 }}>
            {/* Open Python Editor */}
            <Card
              elevation={0}
              sx={{
                flex: 1,
                bgcolor: "surfaceContainerHighest",
                border: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.10)"
                    : "rgba(0,0,0,0.10)",
                transition: "border-color 150ms, background-color 150ms",
                "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
              }}
            >
              <CardActionArea
                onClick={onNavigateToPython}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.75, height: "100%" }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PlayArrowOutlinedIcon sx={{ fontSize: 24, color: "primary.contrastText" }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2">Open Python Editor</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Write and run Python scripts in the sandbox editor.
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>

            {/* Chat with Marie */}
            <Card
              elevation={0}
              sx={{
                flex: 2,
                bgcolor: "surfaceContainerHighest",
                border: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.10)"
                    : "rgba(0,0,0,0.10)",
                transition: "border-color 150ms, background-color 150ms",
                "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
              }}
            >
              <CardActionArea
                onClick={onChatWithMarie}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  px: 2.5,
                  py: 2,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    bgcolor: "secondary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MarieIcon fontSize={48} color="rgba(0,0,0,0.87)" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" component="div">Chat with Marie</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Describe what your script should do — Marie will write it.
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          </Box>
        </Box>
      </BentoCell>

      {/* ── Quick Start (top-right, 1/3 width = col 3) ──────── */}
      <BentoCell surface="surfaceContainer" sx={{ gridColumn: "9 / span 4", gridRow: "1" }}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Start
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Load a starter script and sample image in one click.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {QUICK_START_CARDS.map((card) => {
            const isLoading = loadingId === card.id;
            return (
              <Card key={card.id} elevation={0} sx={{ bgcolor: "surfaceContainerHigh" }}>
                <CardActionArea onClick={() => handleQuickStart(card)} sx={{ p: 1.25 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                    <Typography variant="subtitle2">{card.title}</Typography>
                    {isLoading && <CircularProgress size={14} thickness={5} />}
                  </Box>
                  {!isLoading && (
                    <Typography variant="caption" color="text.secondary">
                      {card.description}
                    </Typography>
                  )}
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
        <Link
          component="button"
          variant="caption"
          onClick={() => onNavigateToScripts(1)}
          sx={{ mt: 1.5, display: "inline-block", textAlign: "left" }}
        >
          View all starter scripts →
        </Link>
      </BentoCell>

      {/* ── AI Prompt Chips (bottom-left, 1/3 width = col 1) ── */}
      <BentoCell surface="surfaceContainerLow" sx={{ gridColumn: "1 / span 3", gridRow: "2" }}>
        <Typography variant="subtitle2" gutterBottom>
          Ask the AI
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Click a prompt to open Marie with a pre-filled message.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {AI_PROMPT_CHIPS.map((prompt) => (
            <Chip
              key={prompt}
              label={prompt}
              size="small"
              clickable
              onClick={() => onAiPrompt(prompt)}
              variant="outlined"
              sx={{ fontSize: "0.75rem", height: 24 }}
            />
          ))}
        </Box>
      </BentoCell>

      {/* ── Featured Scripts (bottom-right, 2/3 = cols 2-3) ─── */}
      {/*
        Same bottom-left → top-right gradient motif as the hero, but bracketed
        one tier lower so this panel's average tone preserves the surface
        hierarchy set up across the bento (hero is the brighter cell).
      */}
      <BentoCell
        surface="surfaceContainer"
        sx={{
          gridColumn: "4 / span 9",
          gridRow: "2",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? `linear-gradient(to top right, ${theme.palette.surfaceContainer}, ${theme.palette.surfaceContainerHigh})`
              : `linear-gradient(to top right, ${theme.palette.surfaceContainer}, #ffffff)`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="subtitle2">Featured Community Scripts</Typography>
          <Link component="button" variant="caption" onClick={onNavigateToCommunity}>
            Browse all →
          </Link>
        </Box>

        {featuredLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3, justifyContent: "center" }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading...</Typography>
          </Box>
        ) : featuredScripts.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 4,
              px: 2,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No community scripts yet.
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Be the first to share one — save a script in the Python tab, then publish it from My Scripts.
            </Typography>
            <Link
              component="button"
              variant="caption"
              onClick={() => onNavigateToScripts(0)}
              sx={{ mt: 0.5 }}
            >
              Go to My Scripts →
            </Link>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1 }}>
            {featuredScripts.map((script) => (
              <Box
                key={script.id}
                role="button"
                tabIndex={0}
                aria-label={`Load ${script.title}`}
                onClick={() => {
                  // Community-sourced load — routes through the Marie
                  // introduce-script endpoint. onNavigateToPython is
                  // redundant here (handleCommunityScriptLoad already
                  // sets the tab to "python" via handleLoadScript) but
                  // we keep it for safety / parity with the starter cells.
                  onLoadCommunityScript(script);
                  onNavigateToPython();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onLoadCommunityScript(script);
                    onNavigateToPython();
                  }
                }}
                sx={{
                  bgcolor: "surfaceContainerHigh",
                  border: "1px solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.10)"
                      : "rgba(0,0,0,0.10)",
                  borderRadius: 2,
                  // overflow: hidden so the rounded corners clip the
                  // full-height image panel on the top-right + bottom-
                  // right cleanly.
                  overflow: "hidden",
                  cursor: "pointer",
                  // Horizontal flex row: left content column (~62%,
                  // flex: 1) + right image panel (~38%, flexBasis on
                  // FeaturedSideImage). The whole row is one click
                  // target — clicking the image loads the script too.
                  display: "flex",
                  flexDirection: "row",
                  // Short-description cards would otherwise be too
                  // stubby for the image panel to read well — pin a
                  // modest floor. The panel stretches to whatever
                  // height the text content produces above this.
                  minHeight: 132,
                  transition: "border-color 150ms, background-color 150ms",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "surfaceContainerHighest",
                  },
                  "&:focus-visible": {
                    outline: "3px solid",
                    outlineColor: "primary.main",
                    outlineOffset: "2px",
                  },
                }}
              >
                {/* Left content column — author row, title, description,
                    rating footer. ~62% width via flex: 1; the image
                    panel takes the remaining ~38%. */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Author row — leads the card (mirrors CommunityCard) */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <AuthorAvatar name={script.author_name} size={28} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: "block", lineHeight: 1.2 }}
                        noWrap
                      >
                        {script.author_name}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6875rem" }}>
                        shared {formatRelativeTime(script.created_at)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mb: 0.5,
                    }}
                  >
                    {script.title}
                  </Typography>

                  {/* Description */}
                  {script.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {script.description}
                    </Typography>
                  )}

                  <Box sx={{ flex: 1 }} />

                  <Divider sx={{ my: 1 }} />

                  {/* Rating row (non-interactive) */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <StarRating value={script.avg_rating} />
                    <Typography variant="caption" color="text.disabled">
                      ({script.rating_count})
                    </Typography>
                  </Box>
                </Box>

                {/* Full-height image panel — paired image or quiet
                    placeholder. Distinct from the Community tab's
                    full-width top banner; on Welcome the featured cards
                    use a right-edge vertical slice instead. */}
                <FeaturedSideImage imageId={script.default_image_id} />
              </Box>
            ))}
          </Box>
        )}
      </BentoCell>
    </Box>
    <VideoModal open={videoModalOpen} onClose={handleModalClose} which={activeVideo} />
    </>
  );
}
